<template>
    <div
        class="horizontal-stepper-renderer"
        :class="`horizontal-stepper-renderer--${normalizedRendererSettings.stepperSize}`"
    >
        <BCard
            no-body
            class="horizontal-stepper-renderer__navigation"
        >
            <nav
                class="horizontal-stepper-renderer__viewport"
                :aria-label="translationOrFallback(
                    'Component(Workflow):progress',
                    'Workflow progress',
                )"
            >
                <ol class="horizontal-stepper-renderer__steps">
                    <li
                        v-for="(step, index) in steps"
                        :key="step.id"
                        :class="stepClasses(step)"
                        class="horizontal-stepper-renderer__step"
                    >
                        <button
                            type="button"
                            class="horizontal-stepper-renderer__trigger"
                            :disabled="isStepNavigationDisabled(step)"
                            :aria-current="step.isOpen ? 'step' : null"
                            @click="openStep(step)"
                        >
                            <span class="horizontal-stepper-renderer__marker">
                                <FeatherIcon
                                    v-if="isCompleted(step)"
                                    icon="CheckIcon"
                                    :size="markerIconSize"
                                />
                                <span v-else>{{ index + 1 }}</span>
                            </span>

                            <span class="horizontal-stepper-renderer__label">
                                <span class="horizontal-stepper-renderer__number">
                                    {{ numberPrefixFor(index) }}
                                </span>
                                <span class="horizontal-stepper-renderer__title">
                                    {{ titleFor(step) }}
                                </span>
                            </span>
                        </button>

                        <span
                            v-if="index < steps.length - 1"
                            class="horizontal-stepper-renderer__connector"
                            aria-hidden="true"
                        />
                    </li>
                </ol>
            </nav>
        </BCard>

        <component
            v-if="activeStep"
            :is="contentContainerComponent"
            v-bind="contentContainerProps"
            :class="contentContainerClasses"
            :data-workflow-step-id="activeStep.id"
        >
            <component
                :is="contentHeaderComponent"
                v-if="showContentHeader"
                class="horizontal-stepper-renderer__content-header"
            >
                <div class="horizontal-stepper-renderer__heading-row">
                    <h4
                        v-if="normalizedRendererSettings.showStepTitle"
                        class="card-title mb-0"
                    >
                        {{ activeStepHeading }}
                    </h4>
                    <WorkflowSaveStatus
                        v-if="showHeaderSaveStatus"
                        :step="activeStep"
                        :show-text="normalizedRendererSettings.showSaveStatusText"
                        class="ml-auto"
                    />
                </div>
                <p
                    v-if="normalizedRendererSettings.showStepDescription && activeStepDescription"
                    class="horizontal-stepper-renderer__description mb-0"
                >
                    {{ activeStepDescription }}
                </p>
            </component>

            <component
                :is="contentBodyComponent"
                :class="contentBodyClasses"
            >
                <WorkflowStepContent
                    :context="context"
                    :step="activeStep"
                    :review-contributions="reviewContributions"
                    :show-completion-action="false"
                    v-on="$listeners"
                />
            </component>

            <component
                :is="contentFooterComponent"
                v-if="showContentFooter"
                class="horizontal-stepper-renderer__content-footer"
            >
                <BButton
                    v-if="showPreviousButton"
                    variant="outline-secondary"
                    @click="openPreviousStep"
                >
                    {{ translationOrFallback('Component(Button):previous-step', 'Previous step') }}
                </BButton>

                <WorkflowStepCompletionButton
                    v-if="hasStandardCompletion"
                    class="ml-auto"
                    :step="activeStep"
                    :label="translationOrFallback(
                        'Component(Button):confirm-and-continue',
                        'Confirm and continue',
                    )"
                    :compact="false"
                    @complete-step="$emit('complete-step', $event)"
                />
            </component>
        </component>
    </div>
</template>

<script>
import {
    BButton,
    BCard,
    BCardBody,
    BCardFooter,
    BCardHeader,
} from 'bootstrap-vue'
import WorkflowStepCompletionButton from './WorkflowStepCompletionButton.vue'
import WorkflowStepContent from './WorkflowStepContent.vue'
import WorkflowSaveStatus from './WorkflowSaveStatus.vue'
import { isForwardNavigationBlockedByInvalidStep } from '../runtime/workflow-navigation'
import {
    resolveStepDescription,
    resolveStepHeading,
    resolveStepNavigationTitle,
    resolveStepNumberPrefix,
    resolveStepTitle,
} from './step-title'

const RENDERER_SETTINGS_DEFAULTS = Object.freeze({
    contentContainer: 'card',
    showStepTitle: true,
    showStepDescription: true,
    showPreviousButton: true,
    contentBordered: false,
    stepperSize: 'sm',
    saveStatus: 'none',
    showSaveStatusText: false,
})

export default {
    name: 'HorizontalStepperRenderer',
    components: {
        BButton,
        BCard,
        BCardBody,
        BCardFooter,
        BCardHeader,
        WorkflowStepCompletionButton,
        WorkflowStepContent,
        WorkflowSaveStatus,
    },
    props: {
        accordion: {
            type: Boolean,
            required: true,
        },
        rendererSettings: {
            type: Object,
            default: () => ({}),
        },
        navigationSettings: {
            type: Object,
            default: () => ({}),
        },
        context: {
            type: Object,
            required: true,
        },
        steps: {
            type: Array,
            required: true,
        },
        reviewContributions: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        normalizedRendererSettings() {
            return {
                ...RENDERER_SETTINGS_DEFAULTS,
                ...this.rendererSettings,
            }
        },
        activeStep() {
            return this.steps.find(step => step.isOpen) || null
        },
        markerIconSize() {
            return this.normalizedRendererSettings.stepperSize === 'md' ? '21' : '18'
        },
        activeStepIndex() {
            return this.activeStep
                ? this.steps.findIndex(step => step.id === this.activeStep.id)
                : -1
        },
        previousStep() {
            for (let index = this.activeStepIndex - 1; index >= 0; index -= 1) {
                if (this.steps[index].isReachable) {
                    return this.steps[index]
                }
            }

            return null
        },
        activeStepTitle() {
            return this.activeStep
                ? resolveStepTitle(
                    this.activeStep,
                    (key, params) => this.translate(key, params),
                    this.stepTextParams,
                )
                : null
        },
        activeStepHeading() {
            return this.activeStepTitle
                ? resolveStepHeading(
                    this.activeStepIndex + 1,
                    this.activeStepTitle,
                    this.translate,
                )
                : null
        },
        activeStepDescription() {
            return this.activeStep
                ? resolveStepDescription(
                    this.activeStep,
                    (key, params) => this.translate(key, params),
                    this.stepTextParams,
                )
                : null
        },
        stepTextParams() {
            const request = (this.context && this.context.request) || {}

            return {
                customer: request.customer && request.customer.title
                    ? request.customer.title
                    : '',
            }
        },
        isCardContainer() {
            return this.normalizedRendererSettings.contentContainer === 'card'
        },
        contentContainerComponent() {
            return this.isCardContainer ? BCard : 'section'
        },
        contentHeaderComponent() {
            return this.isCardContainer ? BCardHeader : 'header'
        },
        contentBodyComponent() {
            return this.isCardContainer ? BCardBody : 'div'
        },
        contentFooterComponent() {
            return this.isCardContainer ? BCardFooter : 'footer'
        },
        contentContainerProps() {
            return this.isCardContainer ? { noBody: true } : {}
        },
        contentContainerClasses() {
            return [
                'horizontal-stepper-renderer__content',
                `horizontal-stepper-renderer__content--${this.normalizedRendererSettings.contentContainer}`,
                {
                    'horizontal-stepper-renderer__content--bordered': this.isCardContainer
                        && this.normalizedRendererSettings.contentBordered,
                },
            ]
        },
        contentBodyClasses() {
            return [
                'horizontal-stepper-renderer__content-body',
                {
                    'pt-2': this.isCardContainer,
                    'pb-2': this.isCardContainer,
                },
            ]
        },
        showContentHeader() {
            return this.normalizedRendererSettings.showStepTitle
                || this.showHeaderSaveStatus
                || (
                    this.normalizedRendererSettings.showStepDescription
                    && Boolean(this.activeStepDescription)
                )
        },
        showHeaderSaveStatus() {
            return this.normalizedRendererSettings.saveStatus === 'header'
        },
        showPreviousButton() {
            return this.normalizedRendererSettings.showPreviousButton && Boolean(this.previousStep)
        },
        hasStandardCompletion() {
            return this.activeStep && this.activeStep.completion.mode === 'button'
        },
        showContentFooter() {
            return this.showPreviousButton || this.hasStandardCompletion
        },
    },
    methods: {
        isCompleted(step) {
            return step.isCompleted && (!step.hasValidationResult || step.isValid)
        },
        openStep(step) {
            if (!this.isStepNavigationDisabled(step) && !step.isOpen) {
                this.$emit('open-step', step.id)
            }
        },
        isStepNavigationDisabled(step) {
            if (!step.isReachable) {
                return true
            }

            if (isForwardNavigationBlockedByInvalidStep({
                steps: this.steps,
                targetStepId: step.id,
                blockForwardOnInvalidSteps: this.navigationSettings.blockForwardOnInvalidSteps,
            })) {
                return true
            }

            const targetIndex = this.steps.findIndex(candidate => candidate.id === step.id)
            const movesForward = this.activeStepIndex >= 0 && targetIndex > this.activeStepIndex
            const activeStepBlocksForwardNavigation = this.activeStep && (
                !this.activeStep.isValid
                || this.activeStep.isBusy
                || this.activeStep.isLoading
                || this.activeStep.isSaving
            )

            return movesForward && activeStepBlocksForwardNavigation
        },
        openPreviousStep() {
            if (this.previousStep) {
                this.$emit('open-step', this.previousStep.id)
            }
        },
        stepClasses(step) {
            return {
                'horizontal-stepper-renderer__step--active': step.isOpen,
                'horizontal-stepper-renderer__step--completed': this.isCompleted(step),
                'horizontal-stepper-renderer__step--disabled': this.isStepNavigationDisabled(step),
            }
        },
        titleFor(step) {
            return resolveStepNavigationTitle(
                step,
                (key, params) => this.translate(key, params),
                this.stepTextParams,
            )
        },
        numberPrefixFor(index) {
            return resolveStepNumberPrefix(
                index + 1,
                this.translate,
            )
        },
        translate(key, params) {
            return typeof this.$t === 'function'
                ? this.$t(key, params)
                : key
        },
        translationOrFallback(key, fallback) {
            const translated = this.translate(key)

            return translated && translated !== key ? translated : fallback
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables/variables';

.horizontal-stepper-renderer__navigation {
    margin-bottom: 1.5rem;
}

.horizontal-stepper-renderer__content-header {
    display: block;
}

.horizontal-stepper-renderer__heading-row {
    display: flex;
    align-items: center;
}

.horizontal-stepper-renderer__description {
    display: block;
    margin-top: 0.35rem;
    color: var(--workflow-secondary-text-color, #6e6b7b);
    font-size: 0.95rem;
    line-height: 1.4;
}

.horizontal-stepper-renderer__content-footer {
    display: flex;
    align-items: center;
}

.horizontal-stepper-renderer__content--plain {
    .horizontal-stepper-renderer__content-header {
        padding-bottom: 1.5rem;
    }

    .horizontal-stepper-renderer__content-footer {
        padding-top: 1.5rem;
        border-top: 1px solid #ebe9f1;
    }
}

.horizontal-stepper-renderer__content--bordered {
    border: 1px solid #d8d6de;

    .horizontal-stepper-renderer__content-header {
        border-bottom: 1px solid #d8d6de;
    }
}

.horizontal-stepper-renderer__viewport {
    overflow-x: auto;
    padding: 1.5rem;
}

.horizontal-stepper-renderer__steps {
    display: flex;
    align-items: center;
    min-width: max-content;
    margin: 0;
    padding: 0;
    list-style: none;
}

.horizontal-stepper-renderer__step {
    display: flex;
    flex: 1 0 11.5rem;
    align-items: center;

    &:last-child {
        flex-grow: 0;
    }
}

.horizontal-stepper-renderer__trigger {
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 0;
    color: #5e5873;
    text-align: left;
    background: transparent;
    border: 0;

    &:not(:disabled) {
        cursor: pointer;
    }

    &:focus {
        outline: none;
    }

    &:focus-visible .horizontal-stepper-renderer__marker {
        box-shadow: 0 0 0 0.2rem rgba(245, 128, 73, 0.2);
    }
}

.horizontal-stepper-renderer__marker {
    display: inline-flex;
    flex: 0 0 2.5rem;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    color: #6e6b7b;
    font-size: 1rem;
    font-weight: 600;
    border: 2px solid #d8d6de;
    border-radius: 50%;
}

.horizontal-stepper-renderer__label {
    display: flex;
    flex-direction: column;
    min-width: 0;
    margin-left: 0.75rem;
    white-space: nowrap;
}

.horizontal-stepper-renderer__number {
    color: #5e5873;
    font-weight: 600;
    line-height: 1.25;
}

.horizontal-stepper-renderer__title {
    margin-top: 0.2rem;
    color: $chemity-accent;
    font-size: 0.875rem;
    line-height: 1.25;
}

.horizontal-stepper-renderer__connector {
    flex: 1 1 auto;
    min-width: 2rem;
    height: 2px;
    margin: 0 1rem;
    background-color: #d8d6de;
}

.horizontal-stepper-renderer__step--completed {
    .horizontal-stepper-renderer__marker {
        color: #28c76f;
        border-color: #28c76f;
    }

    .horizontal-stepper-renderer__connector {
        background-color: rgba(40, 199, 111, 0.45);
    }
}

.horizontal-stepper-renderer__step--active {
    .horizontal-stepper-renderer__marker {
        color: #f58049;
        border-color: #f58049;
    }
}

.horizontal-stepper-renderer__step--disabled {
    .horizontal-stepper-renderer__trigger,
    .horizontal-stepper-renderer__number,
    .horizontal-stepper-renderer__title {
        color: #b9b9c3;
    }

    .horizontal-stepper-renderer__marker {
        color: #b9b9c3;
        border-color: #d8d6de;
    }
}

.horizontal-stepper-renderer__step--completed.horizontal-stepper-renderer__step--disabled {
    .horizontal-stepper-renderer__marker {
        color: #28c76f;
        border-color: #28c76f;
    }
}

.horizontal-stepper-renderer--md {
    .horizontal-stepper-renderer__viewport {
        padding: 1.75rem;
    }

    .horizontal-stepper-renderer__step {
        flex-basis: 13.25rem;
    }

    .horizontal-stepper-renderer__marker {
        flex-basis: 2.875rem;
        width: 2.875rem;
        height: 2.875rem;
        font-size: 1.125rem;
    }

    .horizontal-stepper-renderer__label {
        margin-left: .875rem;
    }

    .horizontal-stepper-renderer__number {
        font-size: 1.125rem;
    }

    .horizontal-stepper-renderer__title {
        margin-top: .25rem;
        font-size: 1rem;
    }

    .horizontal-stepper-renderer__connector {
        min-width: 2.25rem;
        margin-right: 1.125rem;
        margin-left: 1.125rem;
    }
}

@media (max-width: 767.98px) {
    .horizontal-stepper-renderer__viewport {
        padding: 1rem;
    }

    .horizontal-stepper-renderer__step {
        flex-basis: 10.5rem;
    }

    .horizontal-stepper-renderer--md .horizontal-stepper-renderer__step {
        flex-basis: 12rem;
    }
}
</style>
